/**
 * Opzioni dei filtri guidati di /payments.
 *
 * Arrivano da `get_payments_filter_options` come lista denormalizzata, una riga per
 * itinerario visibile con piano. Le tre tendine — viaggio, cliente, collaboratore — sono
 * proiezioni della stessa lista: cosi' le cascate (scelto il collaboratore X restano i suoi
 * viaggi e i suoi clienti) si calcolano in memoria, senza una chiamata per combinazione,
 * e le tendine non possono andare fuori sincrono fra loro.
 */

export const PAYMENTS_FILTER_OPTIONS_QUERY_KEY = 'payments-filter-options';

/** Il server non ne restituisce mai di piu': oltre, `truncated` diventa vero. */
export const FILTER_OPTIONS_LIMIT = 500;

/** Una riga: un itinerario col suo cliente e il suo collaboratore gia' risolti. */
export interface IPaymentsFilterRow {
    itinerary_id: string;
    title: string | null;
    /** Email normalizzata: il cliente non ha un ID (le richieste anonime non hanno user_id). */
    client_key: string | null;
    client_name: string | null;
    client_email: string | null;
    designer_id: string | null;
    designer_name: string | null;
}

export interface IPaymentsFilterOptions {
    rows: IPaymentsFilterRow[];
    /** Righe che soddisfano la ricerca in tutto, non righe restituite. */
    total_count: number;
    /**
     * La lista e' parziale. Da qui il client capisce che non puo' piu' filtrare in memoria
     * e deve girare la ricerca al server, altrimenti cercherebbe dentro un insieme tagliato
     * e concluderebbe che un cliente non esiste solo perche' e' oltre il cinquecentesimo.
     */
    truncated: boolean;
    limit: number;
    query: string | null;
}

export type PaymentsFacet = 'itinerary' | 'client' | 'designer';

/** Una voce di tendina, gia' pronta da mostrare. */
export interface IFacetOption {
    value: string;
    label: string;
    /** Riga secondaria: email del cliente, cliente del viaggio. Disambigua gli omonimi. */
    hint?: string | null;
}

export interface PaymentsFacetSelection {
    itineraryId: string | null;
    clientKey: string | null;
    designerId: string | null;
}

export const EMPTY_SELECTION: PaymentsFacetSelection = {
    itineraryId: null,
    clientKey: null,
    designerId: null,
};

/**
 * Righe compatibili con la selezione, ignorando la facet richiesta.
 *
 * Escludere la propria selezione e' la regola della ricerca a faccette, e non un dettaglio:
 * se le opzioni del collaboratore fossero filtrate anche per il collaboratore scelto, la
 * tendina mostrerebbe quel solo nome e non ci sarebbe piu' modo di cambiarlo senza prima
 * azzerarlo. Ogni tendina resta quindi navigabile per intero, ristretta solo dalle altre.
 */
const rowsFor = (
    rows: IPaymentsFilterRow[],
    selection: PaymentsFacetSelection,
    exclude: PaymentsFacet
): IPaymentsFilterRow[] =>
    rows.filter(
        (row) =>
            (exclude === 'itinerary' ||
                !selection.itineraryId ||
                row.itinerary_id === selection.itineraryId) &&
            (exclude === 'client' ||
                !selection.clientKey ||
                row.client_key === selection.clientKey) &&
            (exclude === 'designer' ||
                !selection.designerId ||
                row.designer_id === selection.designerId)
    );

/** Ordine alfabetico stabile, con l'italiano che ordina le accentate come si aspetta. */
const byLabel = (a: IFacetOption, b: IFacetOption) => a.label.localeCompare(b.label, 'it');

/**
 * Opzioni di una tendina.
 *
 * L'ordinamento qui e' alfabetico, mentre il server sceglie *quali* 500 righe mandare per
 * recenza: sono due decisioni diverse. Il server decide cosa e' rilevante, la UI come si
 * scorre — e una lista di quaranta clienti si scorre per nome, non per data di inserimento.
 */
export const facetOptions = (
    rows: IPaymentsFilterRow[],
    facet: PaymentsFacet,
    selection: PaymentsFacetSelection,
    fallbackLabel: string
): IFacetOption[] => {
    const scoped = rowsFor(rows, selection, facet);
    const seen = new Map<string, IFacetOption>();

    for (const row of scoped) {
        if (facet === 'itinerary') {
            if (seen.has(row.itinerary_id)) continue;
            seen.set(row.itinerary_id, {
                value: row.itinerary_id,
                label: row.title?.trim() || fallbackLabel,
                hint: row.client_name,
            });
            continue;
        }

        if (facet === 'client') {
            // Senza client_key il cliente non e' filtrabile: la riga non ha una travel
            // request con un'email, quindi non esiste un valore da mandare alla RPC.
            if (!row.client_key || seen.has(row.client_key)) continue;
            seen.set(row.client_key, {
                value: row.client_key,
                label: row.client_name?.trim() || row.client_email?.trim() || fallbackLabel,
                hint: row.client_email,
            });
            continue;
        }

        if (!row.designer_id || seen.has(row.designer_id)) continue;
        seen.set(row.designer_id, {
            value: row.designer_id,
            label: row.designer_name?.trim() || fallbackLabel,
        });
    }

    return [...seen.values()].sort(byLabel);
};

/**
 * Ripulisce le selezioni rimaste senza righe dopo un nuovo filtro.
 *
 * Scegliendo il collaboratore Y mentre e' selezionato un viaggio di X, quel viaggio non
 * esiste piu' nell'insieme: lasciarlo attivo darebbe una dashboard sempre vuota senza far
 * capire perche'. Si azzera, e il chip che sparisce lo rende visibile.
 *
 * Non fa nulla quando la lista e' troncata: li' l'assenza di una riga non significa che la
 * combinazione sia impossibile, solo che non e' stata scaricata.
 */
export const pruneSelection = (
    rows: IPaymentsFilterRow[],
    selection: PaymentsFacetSelection,
    truncated: boolean
): PaymentsFacetSelection => {
    if (truncated || rows.length === 0) return selection;

    const matches = (next: PaymentsFacetSelection) =>
        rows.some(
            (row) =>
                (!next.itineraryId || row.itinerary_id === next.itineraryId) &&
                (!next.clientKey || row.client_key === next.clientKey) &&
                (!next.designerId || row.designer_id === next.designerId)
        );

    if (matches(selection)) return selection;

    // Si tolgono le facet meno specifiche per prime: il viaggio e' la scelta piu' precisa
    // e quasi sempre quella appena fatta, quindi e' l'ultima da sacrificare.
    for (const candidate of [
        { ...selection, clientKey: null },
        { ...selection, designerId: null },
        { ...selection, clientKey: null, designerId: null },
        { ...selection, itineraryId: null },
        { ...EMPTY_SELECTION, designerId: selection.designerId },
    ]) {
        if (matches(candidate)) return candidate;
    }

    return EMPTY_SELECTION;
};
